<template>
  <div>
    <!-- No visible UI; logic-only component for Force Update & Sync -->
  </div>
</template>

<script setup lang="ts">
import { watch, onMounted, onUnmounted } from 'vue';
import { useRegisterSW } from 'virtual:pwa-register/vue';
import { syncService } from '@/services/sync/syncService';
import { useAuth } from '@/domains/auth';
import { useStoredUserGuid } from '@/composables/useStoredUserGuid';
import { setLoaderConfig, getResetLoaderConfig } from '@/core/loading/globalLoadingStore';
import { clearStorageAndCachesForUpdate } from '@/utils/clearStorageForUpdate';
import { getEnvironmentConfig } from '@/services/environment';

defineOptions({ name: 'AppUpdater' });

const appendLog = (msg: string, _data?: Record<string, unknown>): void => {
  console.info('[AppUpdater]', msg);
};

// TODO: Refactor after testing.

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

const { actions: authActions } = useAuth();
const { getUserGuid } = useStoredUserGuid();
const { needRefresh, updateServiceWorker } = useRegisterSW({
  immediate: true,
  // No onNeedRefresh toast; we handle needRefresh in a watcher with the full workflow
});

const resetLoaderConfig = getResetLoaderConfig();
let checkIntervalId: ReturnType<typeof setInterval> | null = null;
let initialCheckTimeoutId: ReturnType<typeof setTimeout> | null = null;
// Only run the force-update workflow once per page load to avoid redirect loops.
let workflowAlreadyRun = false;
// Debounce visibility-based check so we don't run it in a loop if visibilitychange fires repeatedly.
const VISIBILITY_DEBOUNCE_MS = 5000;
let lastVisibilityCheckAt = 0;

const logServiceWorkerState = async (label: string): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    console.info('[AppUpdater] SW state:', label, '- Service workers not supported');
    return;
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) {
      console.info('[AppUpdater] SW state:', label, '- No registration');
      return;
    }
    const active = reg.active ? { scriptURL: reg.active.scriptURL, state: reg.active.state } : null;
    const waiting = reg.waiting
      ? { scriptURL: reg.waiting.scriptURL, state: reg.waiting.state }
      : null;
    const installing = reg.installing
      ? { scriptURL: reg.installing.scriptURL, state: reg.installing.state }
      : null;
    console.info('[AppUpdater] SW state:', label, {
      scope: reg.scope,
      needRefresh: needRefresh.value,
      active,
      waiting,
      installing,
    });
  } catch (e) {
    console.warn('[AppUpdater] SW state error:', label, e);
  }
};

const runForceUpdateWorkflow = async (reason: string): Promise<void> => {
  if (workflowAlreadyRun) {
    appendLog(`Workflow SKIPPED (already run this load): ${reason}`);
    return;
  }
  workflowAlreadyRun = true;
  appendLog(`Workflow STARTING: ${reason}`);
  try {
    setLoaderConfig({
      variant: 'fullscreen',
      message: 'Updating...',
      subtitle: 'Syncing your data and loading the latest version',
      backdrop: true,
    });

    // Capture uuid before any clearing so we can redirect to /auth/verify/:uuid
    const uuid = await getUserGuid();
    console.info('[AppUpdater] Captured uuid for redirect:', uuid ?? '(none)');

    await syncService.triggerSync();
    console.info('[AppUpdater] Sync done, logging out...');

    await authActions.logout();
    console.info('[AppUpdater] Logout done, clearing storage and caches...');

    await clearStorageAndCachesForUpdate();

    // Full page redirect so we load the new build. Never use Vue router here:
    // router.replace() would lazy-load route chunks (e.g. OtpVerificationView-xxx.js) whose
    // filenames change each build, causing 404 after caches are cleared and server was updated.
    const path = uuid ? `/auth/verify/${uuid}` : '/';
    const url = `${globalThis.location.origin}${path}`;
    appendLog(`REDIRECTING to ${url}`);
    globalThis.location.replace(url);
  } catch (error) {
    workflowAlreadyRun = false;
    console.error('[AppUpdater] Force update workflow failed:', error);
    resetLoaderConfig(true);
  }
};

const VERSION_CHECK_URL = '/version.json';

// Client version from .env (VITE_APP_VERSION) - compared with server version from version.json.
const getClientVersionFromEnv = (): string =>
  (import.meta.env.VITE_APP_VERSION ?? getEnvironmentConfig().appVersion ?? '').toString().trim();

// Compares .env VITE_APP_VERSION with server version from version.json; runs force-update when versions differ.
const checkServerVersion = async (label: string): Promise<void> => {
  appendLog(`checkServerVersion start label=${label}`, { label });
  if (!navigator.onLine) return;
  try {
    const clientVersion = getClientVersionFromEnv();
    const url = `${globalThis.location.origin}${VERSION_CHECK_URL}?t=${Date.now()}`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) {
      console.info(
        '[AppUpdater] Version check',
        label,
        '-',
        res.status,
        res.statusText,
        '(client/VITE_APP_VERSION:',
        clientVersion,
        ')'
      );
      return;
    }
    const data = (await res.json()) as { version?: string };
    const serverVersion = (data?.version ?? '').toString().trim();
    console.info(
      '[AppUpdater] Version check',
      label,
      '- server (version.json):',
      serverVersion,
      'client (.env VITE_APP_VERSION):',
      clientVersion
    );
    if (serverVersion && clientVersion !== serverVersion) {
      appendLog(
        `version mismatch trigger server=${serverVersion} client=${clientVersion} label=${label}`,
        {
          serverVersion,
          clientVersion,
          label,
        }
      );
      void runForceUpdateWorkflow(
        `version mismatch: server=${serverVersion} client=${clientVersion}`
      );
    }
  } catch (e) {
    console.info(
      '[AppUpdater] Version check',
      label,
      'failed:',
      e instanceof Error ? e.message : e
    );
  }
};

const runUpdateCheckAndLogState = (label: string): void => {
  appendLog(`runUpdateCheckAndLogState label=${label}`);
  void checkServerVersion(label);
  updateServiceWorker()
    .then(() => {
      appendLog(`updateServiceWorker() resolved label=${label}`);
      return logServiceWorkerState(`${label} (after check)`);
    })
    .catch((err) => console.warn('[AppUpdater] Update check failed:', label, err));
};

const schedulePeriodicCheck = (): void => {
  appendLog(`schedulePeriodicCheck entered checkIntervalId=${checkIntervalId}`);
  if (checkIntervalId) return;
  checkIntervalId = setInterval(() => {
    appendLog(`setInterval tick navigator.onLine=${navigator.onLine}`);
    if (navigator.onLine) {
      appendLog('Periodic update check');
      runUpdateCheckAndLogState('periodic');
    }
  }, CHECK_INTERVAL_MS);
  appendLog(`SW update check scheduled every ${CHECK_INTERVAL_MS / 60000} min`);
};

watch(
  needRefresh,
  (value) => {
    if (!value) return;
    appendLog(`needRefresh became true online=${navigator.onLine}`);
    if (!navigator.onLine) {
      console.info('[AppUpdater] needRefresh is true but offline, will run workflow when online');
      return;
    }
    void runForceUpdateWorkflow('needRefresh (SW update)');
  },
  { immediate: false }
);

const onOnline = (): void => {
  if (needRefresh.value && navigator.onLine) {
    void runForceUpdateWorkflow('back online (needRefresh)');
  }
};

const onControllerChange = (): void => {
  console.info('[AppUpdater] Service worker controllerchange - new SW took control');
  void logServiceWorkerState('controllerchange');
};

const onVisibilityChange = (): void => {
  appendLog('visibilitychange fired', {
    visibilityState: document.visibilityState,
    hidden: document.hidden,
  });
  if (document.visibilityState !== 'visible' || !navigator.onLine) return;
  const now = Date.now();
  if (now - lastVisibilityCheckAt < VISIBILITY_DEBOUNCE_MS) {
    appendLog(`visibility check skipped debounce ${VISIBILITY_DEBOUNCE_MS}ms`, {
      msSinceLast: now - lastVisibilityCheckAt,
    });
    return;
  }
  lastVisibilityCheckAt = now;
  appendLog('Tab visible running update check visibility');
  runUpdateCheckAndLogState('visibility');
};

onMounted(() => {
  appendLog('onMounted entered');
  void logServiceWorkerState('mounted');
  globalThis.addEventListener('controllerchange', onControllerChange);
  document.addEventListener('visibilitychange', onVisibilityChange);
  schedulePeriodicCheck();
  globalThis.addEventListener('online', onOnline);
  const initialCheckDelayMs = 3000;
  appendLog(`onMounted scheduling initial check in ${initialCheckDelayMs}ms`, {
    initialCheckDelayMs,
  });
  initialCheckTimeoutId = globalThis.setTimeout(() => {
    appendLog('initialCheck timeout fired');
    initialCheckTimeoutId = null;
    if (navigator.onLine) {
      appendLog(`Initial update check after ${initialCheckDelayMs / 1000}s`);
      runUpdateCheckAndLogState('initial');
    } else {
      appendLog('Initial check skipped offline');
    }
  }, initialCheckDelayMs);
});

onUnmounted(() => {
  appendLog('onUnmounted entered');
  if (initialCheckTimeoutId) {
    clearTimeout(initialCheckTimeoutId);
    initialCheckTimeoutId = null;
  }
  if (checkIntervalId) {
    clearInterval(checkIntervalId);
    checkIntervalId = null;
  }
  document.removeEventListener('visibilitychange', onVisibilityChange);
  globalThis.removeEventListener('online', onOnline);
  globalThis.removeEventListener('controllerchange', onControllerChange);
});
</script>
